import React from 'react';
import { Check, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown } from 'lucide-react';

export const Box = ({children, sx, ...p}) => <div className={`p-4 md:p-8 ${sx?.mb ? 'mb-'+sx.mb : ''}`} style={sx} {...p}>{children}</div>;

export const Typography = ({children, variant, fontWeight, gutterBottom, color, sx, ...p}) => {
    let classes = '';
    if (variant === 'h4') classes = 'text-3xl sm:text-4xl font-black font-montserrat text-[#1E3A8A] mb-2 uppercase tracking-tighter';
    else if (variant === 'h5') classes = 'text-2xl font-bold font-montserrat text-[#1E3A8A] mb-4 mt-8 flex items-center before:content-[""] before:block before:w-1.5 before:h-6 before:bg-[#FACC15] before:mr-3 before:rounded-sm before:-skew-x-[16deg]';
    else if (variant === 'h6') classes = 'text-xl font-bold font-montserrat text-[#1E3A8A] mb-4 mt-8 flex items-center before:content-[""] before:block before:w-1.5 before:h-5 before:bg-[#FACC15] before:mr-3 before:rounded-sm before:-skew-x-[16deg]';
    else if (variant === 'body2') classes = 'text-sm text-gray-500 font-inter leading-relaxed';
    else classes = 'text-base font-inter text-gray-800';
    if (gutterBottom) classes += ' mb-6';
    return <div className={classes} style={sx} {...p}>{children}</div>;
};

export const TextField = ({label, value, onChange, type, multiline, rows, sx, fullWidth}) => {
    const inputClasses = "w-full bg-gray-50/50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:bg-white focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all font-inter placeholder-gray-400 focus:outline-none";
    return (
        <div className={`mb-3 ${fullWidth ? 'w-full' : ''}`} style={sx}>
            {label && <label className="block text-[11px] font-bold font-montserrat uppercase tracking-widest text-[#1E3A8A] mb-1.5 opacity-80">{label}</label>}
            {multiline ? 
                <textarea value={value} onChange={onChange} rows={rows||3} className={inputClasses} /> :
                <input type={type||"text"} value={value} onChange={onChange} className={inputClasses} />
            }
        </div>
    );
};

export const Button = ({children, onClick, startIcon, variant, color, sx}) => {
    const isPrimary = variant === 'contained' || !variant || color === 'primary';
    const isError = color === 'error';
    
    let baseClass = "relative overflow-hidden font-montserrat font-black uppercase text-xs tracking-wider px-6 py-3 inline-flex items-center justify-center gap-2 rounded-xl transition-all active:scale-95";
    
    if (isPrimary) {
        baseClass += " bg-gradient-to-r from-[#FACC15] to-[#fde047] text-[#1E3A8A] shadow-[0_4px_14px_rgba(250,204,21,0.3)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.5)] border-b-2 border-yellow-500";
    } else if (isError) {
        baseClass += " bg-white border-2 border-red-500 text-red-600 hover:bg-red-50 shadow-sm";
    } else {
        baseClass += " bg-white border-2 border-gray-200 text-[#1E3A8A] hover:bg-gray-50 shadow-sm";
    }

    return (
        <button onClick={onClick} className={`${baseClass} ${sx?.mb ? 'mb-'+sx.mb : ''}`} style={sx}>
            {startIcon && <span className="opacity-90 flex items-center justify-center">{startIcon}</span>}
            <span className="relative z-10">{children}</span>
        </button>
    );
};

export const IconButton = ({children, onClick, color, size, disabled}) => (
    <button onClick={onClick} disabled={disabled} className={`p-2 rounded-lg transition-all active:scale-90 ${color==='error'?'text-red-500 hover:bg-red-50 hover:text-red-700': 'text-[#1E3A8A] hover:bg-blue-50'} ${size==='small'?'scale-90':''} disabled:opacity-30 disabled:cursor-not-allowed`}>
        {children}
    </button>
);

export const TableContainer = ({children, sx}) => (
    <div className="bg-white rounded-2xl shadow-[0px_4px_20px_rgba(0,0,0,0.04)] border border-gray-200/60 overflow-hidden mb-6 transition-all hover:shadow-[0px_4px_25px_rgba(0,0,0,0.06)]" style={sx}>
        <div className="overflow-x-auto">
            {children}
        </div>
    </div>
);

export const Table = ({children, size}) => <table className={`w-full text-left text-sm border-separate border-spacing-0 ${size==='small'?'text-[13px]':''}`}>{children}</table>;
export const TableHead = ({children}) => <thead className="sticky top-0 z-40 shadow-sm relative">{children}</thead>;
export const TableRow = ({children}) => <tr className="group transition-colors last:border-0 border-b border-gray-100 hover:bg-blue-50/40">{children}</tr>;
export const TableBody = ({children}) => <tbody className="bg-white">{children}</tbody>;
export const TableCell = ({children, className}) => (
    <th className={`p-4 align-middle text-gray-700 font-inter font-medium border-b border-gray-100 bg-inherit sticky top-[-1.5rem] z-20 ${className||''}`}>
        {children}
    </th>
);

export const Paper = ({children, sx}) => <div style={sx}>{children}</div>;

export const Select = ({value, onChange, children, style, multiple, disabled}) => (
    <div className="relative group">
        <select 
            multiple={multiple} 
            value={value} 
            onChange={onChange} 
            disabled={disabled}
            style={style} 
            className={`w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-inter text-gray-700 focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] transition-all outline-none ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''} ${multiple?'':'appearance-none pr-10 hover:border-gray-300'}`}
        >
            {children}
        </select>
        {!multiple && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-[#1E3A8A] transition-colors">
                <ChevronDown size={16} />
            </div>
        )}
    </div>
);
export const MenuItem = ({value, children}) => <option value={value}>{children}</option>;
export const FormControl = ({children, sx}) => <div className="mb-2 w-full" style={sx}>{children}</div>;
export const InputLabel = ({children}) => (
    <label className="block text-[10px] font-black font-montserrat uppercase tracking-[0.1em] text-[#1E3A8A] mb-1.5 ml-1 opacity-70">
        {children}
    </label>
);

export const Snackbar = ({children, open, onClose}) => open ? (
    <div className="fixed bottom-6 right-6 z-[9999] animate-in fade-in slide-in-from-bottom-4 cursor-pointer" onClick={onClose}>
        {children}
    </div>
) : null;

export const Alert = ({children, severity}) => (
    <div className={`px-5 py-3.5 rounded-xl shadow-xl border text-sm font-bold font-montserrat flex items-center gap-3 backdrop-blur-md ${severity==='success'?'bg-white/90 border-green-200 text-green-700':'bg-white/90 border-red-200 text-red-700'}`}>
        {severity==='success' && <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></div>}
        {children}
    </div>
);

export const Divider = ({sx}) => <hr className="my-8 border-gray-200/60" style={sx} />;

export const Tabs = ({value, onChange, children}) => (
    <div className="flex gap-2 p-1.5 bg-gray-100/60 rounded-xl mb-8 w-max overflow-x-auto shadow-inner border border-gray-200/50">
        {React.Children.map(children, (c, i) => React.cloneElement(c, {active: value === i, onClick: () => onChange(null, i)}))}
    </div>
);

export const Tab = ({label, active, onClick}) => (
    <button onClick={onClick} className={`px-6 py-2.5 rounded-lg text-xs font-black font-montserrat uppercase tracking-widest transition-all duration-300 ${ 
        active 
        ? 'bg-white text-[#1E3A8A] shadow-[0_2px_12px_rgba(0,0,0,0.08)] scale-100' 
        : 'text-gray-500 hover:text-[#1E3A8A] hover:bg-white/50 scale-95 opacity-80 hover:opacity-100 hover:scale-100'
    }`}>
        {label}
    </button>
);

export const Checkbox = ({checked, onChange, disabled}) => {
    return (
        <div 
            onClick={() => !disabled && onChange && onChange({target:{checked: !checked}})}
            className={`flex items-center justify-center w-5 h-5 rounded-[4px] border-2 transition-all ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'} ${checked ? (disabled ? 'bg-gray-400 border-gray-400' : 'bg-[#1E3A8A] border-[#1E3A8A]') : 'bg-white border-gray-300 hover:border-[#1E3A8A]/50'}`}
        >
            <Check size={14} className={`text-white transition-opacity duration-200 ${checked ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3} />
        </div>
    );
};

export const FormControlLabel = ({control, label, sx}) => (
    <label className="flex items-center gap-3 cursor-pointer text-sm font-inter text-gray-700 select-none py-1" style={sx}>
        {control} <span className="font-bold">{label}</span>
    </label>
);

export const Dialog = ({open, onClose, children}) => open ? (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95">
            {children}
        </div>
    </div>
) : null;

export const DialogTitle = ({children}) => <h2 className="px-6 py-5 text-xl font-black font-montserrat uppercase tracking-wider text-[#1E3A8A] border-b border-gray-100 bg-gray-50/50">{children}</h2>;
export const DialogContent = ({children}) => <div className="p-6 overflow-y-auto space-y-4">{children}</div>;
export const DialogActions = ({children}) => <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 gap-3 flex justify-end">{children}</div>;

export const TableSortLabel = ({active, direction, onClick, children}) => (
    <button 
        onClick={onClick} 
        className={`flex items-center gap-2 px-3 py-1.5 -ml-3 rounded-lg transition-all duration-200 font-bold uppercase tracking-widest text-[11px] select-none focus:outline-none ${ 
            active 
            ? 'bg-blue-50/50 text-[#1E3A8A] shadow-sm ring-1 ring-[#1E3A8A]/20' 
            : 'text-gray-500 hover:bg-gray-100 hover:text-[#1E3A8A]'
        }`}
    >
        {children} 
        <span className={`flex transition-transform ${active ? 'opacity-100 text-[#FACC15]' : 'opacity-30 text-gray-400'}`}>
            {active ? (direction === 'asc' ? <ArrowUp size={14} strokeWidth={3} /> : <ArrowDown size={14} strokeWidth={3} />) : <ArrowUpDown size={14} />}
        </span>
    </button>
);

export const Grid = ({container, item, spacing, children, sx, className}) => {
    if (container) return <div className={`flex flex-wrap gap-4 items-center ${className||''}`} style={sx}>{children}</div>;
    return <div className={`flex-1 min-w-[200px] ${className||''}`} style={sx}>{children}</div>;
};
